import { isAxiosError } from 'axios';

import { getUser } from '../../utilities/authentication';
import { isUUID } from '../../utilities';

import { CancellationReasonMaster, ConsentType, TransactionDelegateAction, TransactionDelegateRequest, TransactionInfo, OnetrustStatusModel, VerifySignerResponse } from './types';
import api from '../api';
import { User } from '../authen';

interface TransactionIdTypeParams {
  transactionId: string;
}

export const downloadPdf = async (params: TransactionIdTypeParams): Promise<Blob> => {
  const { transactionId } = params;
  if (!isUUID(transactionId)) return Promise.reject("transactionId is not UUID");

  const res = await api.get<Blob>(
    `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/file`,
    {
      responseType: 'arraybuffer',
      params
    }
  );
  
  const blob = new Blob([res.data], { type: 'application/pdf' });
  return blob;
}

export const cancelPendingTransaction = async ({ transactionId, reason }: TransactionIdTypeParams & { reason: string }): Promise<boolean> => {
  if (!isUUID(transactionId)) return Promise.reject("transactionId is not UUID");

  const user = getUser() || {} as User;
  let data = JSON.stringify({
    reason,
    cancelBy: `${user.id} - ${user.name}`
  });
  const res = await api
    .delete<boolean>(
      `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/transaction/${transactionId}`,
      {
        headers: {
          'Content-Type': 'application/json', 
          'Accept': 'text/plain'
        },
        data
      }
    );

  return res.data;
}

export const updateNoteByTransaction = async ({ transactionId, note }: TransactionIdTypeParams & { note: string }): Promise<boolean> => {
  if (!isUUID(transactionId)) return Promise.reject("transactionId is not UUID");

  const user = getUser() || {} as User;
  try
  {
    const res = await api.patch<boolean>(
      `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/transaction/note/${transactionId}`,
      {
        note,
        notedBy: `${user.name}`
      },
      {
        headers: {
          'Content-Type': 'application/json', 
          'Accept': 'text/plain'
        }
      }
    );
    return res.data;
  } catch (e) {
    console.log(e);
    return false;
  }
}

export const getCancellationReasonsMaster = async (type?: ConsentType): Promise<CancellationReasonMaster[]> => {
  const response = await api
    .get<CancellationReasonMaster[]>(
      `${process.env.REACT_APP_ECONSENT_SUFFIX}/master/reason/cancel`,
      {
        params: { type }
      }
    );
  return response.data;
}

export const updateTransactionDelegate = async (
  transactionId: string,
  request: TransactionDelegateRequest
): Promise<{ success: boolean; error?: string }> => {
  if (!isUUID(transactionId)) return Promise.reject({ success: false, error: "transactionId is not UUID" });
  
  try {
    const payload: TransactionDelegateRequest = {
      action: request.action || TransactionDelegateAction.Replace,
      ...request,
    };

    const res = await api.post(
      `${process.env.REACT_APP_ECONSENT_SUFFIX}/transaction/delegate/${transactionId}`,
      payload,
    );
    
    if (res.status === 200) return { success: true };
    return { success: false, error: res.data?.message || "Unknown error" };
  } catch (err: any) {
    return { success: false, error: err.response?.data?.message || err.message || "Unknown error" };
  }
}

export const getTransactionInfo = async (transactionId: string): Promise<TransactionInfo> => {
  const response = await api.get<TransactionInfo>(
    `${process.env.REACT_APP_ECONSENT_SUFFIX}/transaction-info/${transactionId}`
  );
  return response.data;
}

export const requestOnetrustStatus = async (params: {
  transactionId: string;
}) : Promise<OnetrustStatusModel> => {
  const { transactionId } = params;
  if(!isUUID(transactionId)) return Promise.reject("transactionId is not UUID");

  const res = await api.get<OnetrustStatusModel>(
    `${process.env.REACT_APP_ECONSENT_SUFFIX}/econsent/onetrust`,
    { params }
  );

  return res.data;
}

export async function verifySignerByCandidate(
  transactionId: string,
  candidateId: string,
  username: string,
  password: string
): Promise<VerifySignerResponse> {
  try {
    const response = await api.post<VerifySignerResponse>(
      `${process.env.REACT_APP_ECONSENT_SUFFIX}/transactions/${transactionId}/candidates/${candidateId}/verify`,
      { username, password },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_ECONSENT_API_KEY}`,
        },
      }
    );
    return response.data;
  } catch (err: any) {
    if (!isAxiosError(err)) throw err;

    switch (err.response?.status) {
      case 401:
        throw new Error(err.response?.data ?? "Invalid username or password.");
      case 403:
        throw new Error("Employee code does not match this signer. Please sign in as the correct user.");
      case 404:
        throw new Error("Transaction or candidate not found.");
      case 500:
        throw new Error("An unexpected error occurred. Please try again later.");
      default:
        throw new Error(err.response?.data ?? "Verification failed.");
    }
    throw err;
  }
}

export * from './clinical.services';
export * from './general.services';
export * from './dynamic-form.service';
export * from './user-locations.services';